import { createContext } from 'preact';
import { useCallback, useEffect, useState } from 'preact/hooks';
import { get, post, clearOfflineCopy } from './api.js';
import { flushOutbox, clearOutbox, pendingPaid } from './outbox.js';
import { useRoute, Link, match, navigate } from './router.js';
import { Setup } from './screens/Setup.jsx';
import { Login } from './screens/Login.jsx';
import { NewPassword } from './screens/NewPassword.jsx';
import { Users } from './screens/Users.jsx';
import { Bookings } from './screens/Bookings.jsx';
import { BookingForm } from './screens/BookingForm.jsx';
import { ToursOffice } from './screens/ToursOffice.jsx';
import { DepartureOffice } from './screens/DepartureOffice.jsx';
import { DepartureGuide } from './screens/DepartureGuide.jsx';
import { ToursGuide } from './screens/ToursGuide.jsx';
import { Calendar } from './screens/Calendar.jsx';
import { EquipmentEntry } from './screens/EquipmentEntry.jsx';
import { Inventory } from './screens/Inventory.jsx';
import { Equipment } from './screens/Equipment.jsx';
import { OfflineBar } from './ui/OfflineBar.jsx';

export const SessionContext = createContext(null);

// One flush of the outbox at a time; start-up, 'online', server answers and sign-out share it. Resolves with how many were sent.
let sending = null;
const sendOutbox = () => (sending ??= flushOutbox().catch(() => 0).finally(() => { sending = null; }));

// Each row: a path pattern, the guide shell's bar title, and a screen per role.
export const ROUTES = [
  { pattern: '/', title: 'Kalendar', office: Calendar, guide: Calendar },
  { pattern: '/rezervacije', title: 'Rezervacije', office: Bookings },
  { pattern: '/rezervacije/nova', title: 'Nova rezervacija', office: BookingForm },
  { pattern: '/rezervacije/:id', title: 'Rezervacija', office: BookingForm },
  { pattern: '/ture', title: 'Ture', office: ToursOffice, guide: ToursGuide },
  { pattern: '/ture/:id', title: 'Tura i grupa', office: DepartureOffice, guide: DepartureGuide },
  { pattern: '/ture/:id/oprema', title: 'Upis opreme', guide: EquipmentEntry },
  { pattern: '/ture/:id/popis', title: 'Popis opreme', guide: Inventory },
  { pattern: '/oprema', title: 'Oprema', office: Equipment, guide: Equipment },
  { pattern: '/vodici', title: 'Vodiči', office: Users },
  { pattern: '/lozinka', title: 'Lozinka', office: NewPassword, guide: NewPassword },
];

const OFFICE_NAV = [['/', 'Kalendar'], ['/rezervacije', 'Rezervacije'], ['/ture', 'Ture'], ['/oprema', 'Oprema'], ['/vodici', 'Vodiči']];
const GUIDE_NAV = [['/', 'Kalendar'], ['/ture', 'Ture'], ['/oprema', 'Oprema']];

const isCurrent = (href, path) => (href === '/' ? path === '/' : path === href || path.startsWith(href + '/'));

function NavLinks({ items, path }) {
  return items.map(([href, label]) => (
    <Link key={href} href={href} aria-current={isCurrent(href, path) ? 'page' : undefined}>{label}</Link>
  ));
}

function resolve(role, path, query) {
  for (const r of ROUTES) {
    const params = match(r.pattern, path);
    if (!params) continue;
    const Screen = r[role];
    if (!Screen) {
      return { title: r.title, body: <p class="empty">Ovaj ekran nije za tvoju ulogu. <Link href="/">Na kalendar</Link></p> };
    }
    return { title: r.title, body: <Screen key={path} params={params} query={query} /> };
  }
  return { title: 'Wild Serbia', body: <p class="empty">Ta stranica ne postoji. <Link href="/">Na kalendar</Link></p> };
}

function OfficeShell({ user, signOut, path, body }) {
  return (
    <div class="office">
      <header class="topbar">
        <Link href="/" class="wordmark">Wild Serbia</Link>
        <nav class="topnav" aria-label="Glavni meni"><NavLinks items={OFFICE_NAV} path={path} /></nav>
        <div class="who">
          <span class="who-name">{user.name}</span>
          <Link href="/lozinka">Promeni lozinku</Link>
          <button class="btn-bar" onClick={signOut}>Izađi</button>
        </div>
      </header>
      <OfflineBar />
      <main class="page">{body}</main>
    </div>
  );
}

function GuideShell({ signOut, path, title, body }) {
  const roots = GUIDE_NAV.map(([href]) => href);
  const back = roots.includes(path) ? null : path.replace(/\/[^/]*\/?$/, '') || '/';
  return (
    <div class="guide">
      <header class="guidebar">
        {back && <Link href={back} class="back" aria-label="Nazad">‹</Link>}
        <span class="bar-title">{title}</span>
        <Link href="/lozinka" class="bar-link">Promeni lozinku</Link>
        <button class="btn-bar" onClick={signOut}>Izađi</button>
      </header>
      <OfflineBar />
      <main class="guide-page">{body}</main>
      <nav class="bottombar" aria-label="Glavni meni"><NavLinks items={GUIDE_NAV} path={path} /></nav>
    </div>
  );
}

function Shell({ user, signOut }) {
  const { path, query } = useRoute();
  const { title, body } = resolve(user.role, path, query);
  return user.role === 'office'
    ? <OfficeShell user={user} signOut={signOut} path={path} body={body} />
    : <GuideShell signOut={signOut} path={path} title={title} body={body} />;
}

export function App() {
  const [session, setSession] = useState(null); // { setupNeeded, user }
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0); // bumped when the signal comes back; screens reload on it
  const user = session?.user;

  const refresh = useCallback(async () => {
    try {
      setSession(await get('/session'));
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  const signOut = useCallback(async () => {
    await sendOutbox();
    const unsent = (await pendingPaid().catch(() => new Set())).size > 0;
    if (unsent && !confirm('Imaš neposlata plaćanja. Ako izađeš, neće biti poslata. Izađi svejedno?')) return;
    try {
      await post('/logout');
    } catch {
      // already signed out, or no signal: the local copy goes either way
    }
    await clearOfflineCopy();
    await clearOutbox().catch(() => {});
    navigate('/', { replace: true });
    await refresh();
  }, [refresh]);

  useEffect(() => { refresh(); }, [refresh]);

  // A 401 from any call means the session is gone (expired, or reset by the office): back to sign-in.
  useEffect(() => {
    window.addEventListener('api-unauthorized', refresh);
    return () => window.removeEventListener('api-unauthorized', refresh);
  }, [refresh]);

  // Back online, or an answer came from the server: send the marks that waited, then reload what the screen shows.
  // (The answer path also covers an app opened with no signal, where Firefox may never fire 'online'.)
  useEffect(() => {
    if (!user) return;
    const online = async () => {
      await sendOutbox();
      setTick((n) => n + 1);
    };
    const answer = async (e) => {
      if (!e.detail.offline && (await sendOutbox()) > 0) setTick((n) => n + 1);
    };
    window.addEventListener('online', online);
    window.addEventListener('api-answer', answer);
    return () => {
      window.removeEventListener('online', online);
      window.removeEventListener('api-answer', answer);
    };
  }, [user?.id]);

  // A guide's app: ask to keep its storage; with signal, send waiting marks, then fetch every tour they are on into the offline copy.
  useEffect(() => {
    if (user?.role !== 'guide' || user.mustChange) return;
    navigator.storage?.persist?.().catch(() => {});
    if (!navigator.onLine) return;
    (async () => {
      await sendOutbox();
      const r = await get('/departures');
      if (r.offline) return;
      for (const d of r.departures) if (d.mine) await get(`/departures/${d.id}`);
    })().catch(() => {});
  }, [user?.id, user?.role, user?.mustChange]);

  let screen;
  if (!session) {
    screen = error
      ? <main class="page"><p class="alert" role="alert">{error}</p><button class="btn" onClick={refresh}>Pokušaj ponovo</button></main>
      : null;
  } else if (session.setupNeeded) {
    screen = <Setup />;
  } else if (!session.user) {
    screen = <Login />;
  } else if (session.user.mustChange) {
    screen = <NewPassword forced />;
  } else {
    screen = <Shell user={session.user} signOut={signOut} />;
  }

  return (
    <SessionContext.Provider value={{ user: user ?? null, refresh, signOut, tick }}>
      {screen}
    </SessionContext.Provider>
  );
}
